import {Component, Plugins, Type, withMeta} from '@packages/systems/wfdl';
import {
  EBoolean,
  EData,
  EElement,
  EEnum,
  ELiteral,
  EList,
  ENumber,
  ERecord,
  EText,
  EUnion,
  Expression,
  TElement,
  TList,
  TNumber,
  TText,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {cleanHref} from '@packages/systems/core/utils/HtmlUtils';
import {evalVisibilityConditions} from '@packages/systems/render';
import {
  formatEmail,
  formatPhone,
} from '@packages/systems/dynamo/utils/DynamoFormattingUtils';
import {getBoolean, getNumber, getObject} from '@packages/utilities/json';
import {LINK_TYPES} from '@packages/shared/render/plugins/Basic/dataTypes/Link/LinkTypes';
import {
  defaultImage,
  defaultLink,
  defaultString,
  defaultNumber,
  defaultBoolean,
  PropType,
} from './constants';
import {
  PropValues,
  SerializeContext,
  SerializeInput,
  CodeComponentProp,
} from './types';
import {
  assertNever,
  parseArray,
  parseImage,
  parseLink,
  parseString,
  parseNumber,
  parsePropSettings,
  parseBoolean,
  toJson,
  parseTextNodeDefaultValue,
  parseRichTextDefaultValue,
} from './utils';
import {parsePropertyLabel} from '@packages/systems/user-components/utils';
import {createStyleVariantType} from '@packages/systems/component-style-variants';
import {TBoolean} from '@packages/systems/plugins-utils/Boolean';
export type ReifyContext = {
  initialId: string;
};

/**
 * Map of Code Component PropType to WFDL dataType
 * with reify/serialize functions and metadata.
 */
export const propTypeMap: {
  [key in PropType]: {
    /**
     * WFDL dataType getter
     */
    getType: (opts: {
      prop: CodeComponentProp;
      pluginId: string;
      componentId: string;
      propId: string;
    }) => Type;
    /**
     * Whether the prop is bindable
     */
    isBindable: boolean;
    /**
     * Whether the prop can be localized.
     * This only affects overrides of the property
     * until we build support to localize defaults of library component props.
     */
    isLocalizable: boolean;
    /**
     * Serialize the prop value to a JSON value
     */
    serialize: (
      value: SerializeInput,
      context: SerializeContext
    ) => PropValues<undefined>[key]; // NodeType is undefined in the context of serialization
    /**
     * Reifies the default prop value to a literal Expression compatible with
     * the corresponding WFDL dataType.
     */
    reifyDefaultValue: (
      prop: CodeComponentProp,
      context: ReifyContext
    ) => Expression;
    /**
     * For slot-type only props: returns the corresponding Slot expression
     */
    getSlotExpression?: (
      propId: string,
      prop: CodeComponentProp
    ) => Expression | null;
    /**
     * For Variant props only: returns the corresponding style variant map
     */
    getVariants?: (
      propId: string,
      prop: CodeComponentProp
    ) => Component.Variants | null;
  };
} = {
  [PropType.Text]: {
    getType: () => TText,
    isBindable: true,
    isLocalizable: true,
    reifyDefaultValue: (prop: CodeComponentProp) =>
      EText(parseString(prop.defaultValue) ?? defaultString),
    serialize: (value: SerializeInput) => parseString(value) ?? defaultString,
  },
  [PropType.Id]: {
    getType: () =>
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('IdTextInput'))),
    isBindable: true,
    isLocalizable: true,
    reifyDefaultValue: () => EText(defaultString),
    serialize: (value: SerializeInput) => parseString(value) ?? defaultString,
  },
  [PropType.Link]: {
    getType: () =>
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('Link'))),
    isBindable: true,
    isLocalizable: true,
    reifyDefaultValue: () =>
      ELiteral({
        name: qualify(Plugins.name('Basic'), typeName('Link')),
        value: {mode: LINK_TYPES.EXTERNAL, url: '#'},
      }),
    serialize: (value: SerializeInput, context: SerializeContext) => {
      const link = parseLink(value);
      if (!link) return defaultLink;

      const makeLink = (
        href: string | undefined | null
      ): PropValues[PropType.Link] => {
        return href
          ? {
              href: context.sanitizeHref(href),
              target: link.target,
              preload: link.preload,
            }
          : defaultLink;
      };

      const {mode} = link;
      switch (mode) {
        case LINK_TYPES.EXTERNAL: {
          return makeLink(link.url ? cleanHref(link.url) : null);
        }
        case LINK_TYPES.EMAIL: {
          const href = formatEmail(
            link.email || '',
            encodeURIComponent(link.subject || ''),
            'href'
          );
          return makeLink(href);
        }
        case LINK_TYPES.TELEPHONE: {
          const href = link.tel ? formatPhone(link.tel, 'href') : null;
          return makeLink(href);
        }
        case LINK_TYPES.SECTION: {
          const idAttr = link.domId ? context.getIdAttr(link.domId) : null;
          const href = idAttr ? `#${idAttr}` : null;
          return makeLink(href);
        }
        case LINK_TYPES.PAGE: {
          const href = link.pageId
            ? context.getPageHref({pageId: link.pageId})
            : null;
          return makeLink(href);
        }
        case LINK_TYPES.DYNPAGE: {
          const href = link.pageSlug
            ? context.getPageHref({pageSlug: link.pageSlug})
            : null;
          return makeLink(href);
        }
        case LINK_TYPES.FILE: {
          const href = link.assetId
            ? context.getAsset(link.assetId)?.src
            : link.assetUrl
              ? link.assetUrl
              : null;
          return makeLink(href);
        }
        case LINK_TYPES.POPOVER:
        case LINK_TYPES.MODAL: {
          // TODO: preserve Popover/Modal Link semantics in code component props (https://linear.app/webflow/issue/DES-7847/preserve-popover-link-semantics-in-code-component-props-and-devlink)
          return defaultLink;
        }
        default: {
          assertNever(mode);
          return defaultLink;
        }
      }
    },
  },
  [PropType.Image]: {
    getType: () =>
      TTypeConstructor(qualify(Plugins.name('Asset'), typeName('Image'))),
    isBindable: true,
    isLocalizable: true,
    // Default value is not supported for Image props, but we provide a fallback value
    reifyDefaultValue: () =>
      ELiteral({
        name: qualify(Plugins.name('Asset'), typeName('Image')),
        value: {id: 'plugins/Basic/assets/placeholder.svg'},
      }),
    serialize: (value: SerializeInput, context: SerializeContext) => {
      const image = parseImage(value);
      if (!image) return defaultImage;
      if (image.url) {
        return {
          src: image.url,
          ...(image.alt ? {alt: image.alt} : {}),
        };
      }

      const asset = image.id ? context.getAsset(image.id) : undefined;
      if (!asset) return defaultImage;
      return {
        src: asset.src,
        ...(asset.alt ? {alt: asset.alt} : {}),
      };
    },
  },
  [PropType.Visibility]: {
    getType: () =>
      TTypeConstructor(
        qualify(Plugins.name('Visibility'), typeName('VisibilityConditions'))
      ),
    isBindable: true,
    isLocalizable: true,
    reifyDefaultValue: (prop: CodeComponentProp) => {
      const jsonValue = toJson(prop.defaultValue);
      const boolean =
        jsonValue !== undefined ? getBoolean(jsonValue) : undefined;
      return EList([EBoolean(typeof boolean === 'boolean' ? boolean : true)]);
    },
    serialize: (value: SerializeInput) => {
      return evalVisibilityConditions(parseArray(value));
    },
  },
  [PropType.Slot]: {
    getType: () =>
      TTypeConstructor(qualify(Plugins.name('Slots'), typeName('SlotContent'))),
    isBindable: false,
    isLocalizable: false,
    reifyDefaultValue: () => EList([]),
    // We don't serialize slot props
    serialize: () => undefined,
    getSlotExpression: (propId: string, prop: CodeComponentProp) => {
      const propName = prop.name;

      const displayName = parsePropertyLabel(
        prop.displayName || prop.name
      ).propertyName;
      return EElement({
        id: propId,
        type: qualify(Plugins.name('Slots'), Component.name('Slot')),
        data: ERecord({
          slot: EText(propName), // 'slot' must use the prop name
          name: EText(displayName), // 'name' must use the display name because it's shown in the UI
          children: EData([propId]),
        }),
      });
    },
  },
  [PropType.RichText]: {
    getType: () =>
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('RichTextChildren'))
      ),
    isBindable: true,
    isLocalizable: true,
    reifyDefaultValue: (prop: CodeComponentProp, {initialId}) => {
      const parsedString = parseString(prop.defaultValue);
      return parseRichTextDefaultValue(
        parsedString ?? defaultString,
        initialId
      );
    },
    // We don't serialize slot-type props
    serialize: () => undefined,
    getSlotExpression: (propId: string, prop: CodeComponentProp) => {
      return EElement({
        id: propId,
        type: qualify(Plugins.name('Basic'), Component.name('RichText')),
        data: ERecord({
          rich: EBoolean(true),
          tag: EEnum('div'),
          slot: EText(prop.name), // 'slot' must use the prop name
          children: EData([propId]),
        }),
      });
    },
  },
  [PropType.Number]: {
    getType: ({prop}) => {
      const settings = parsePropSettings(prop.settings, prop.type);
      if (
        !settings ||
        !('min' in settings || 'max' in settings || 'decimals' in settings)
      )
        return TNumber;

      const {min, max, decimals} = settings;
      return withMeta(
        {
          value: {
            ...(min !== undefined ? {min} : {}),
            ...(max !== undefined ? {max} : {}),
            ...(decimals !== undefined ? {precision: decimals} : {}),
          },
        },
        TNumber
      );
    },
    isBindable: true,
    isLocalizable: true,
    reifyDefaultValue: (prop: CodeComponentProp) => {
      const jsonValue = toJson(prop.defaultValue);
      const number = jsonValue !== undefined ? getNumber(jsonValue) : undefined;
      return ENumber(
        typeof number === 'number' && !Number.isNaN(number)
          ? number
          : defaultNumber
      );
    },
    serialize: (value: SerializeInput) => parseNumber(value) ?? defaultNumber,
  },
  [PropType.Variant]: {
    getType: ({pluginId, componentId, propId}) =>
      createStyleVariantType(pluginId, componentId, propId),
    isBindable: true,
    isLocalizable: true,
    reifyDefaultValue: (prop: CodeComponentProp) => {
      const settings = parsePropSettings(prop.settings, prop.type);
      const options = settings && 'options' in settings ? settings.options : [];
      const defaultValue = parseString(prop.defaultValue);
      const option =
        defaultValue && options.includes(defaultValue)
          ? defaultValue
          : (options[0] ?? 'base');
      return ELiteral({
        name: qualify(Plugins.name('Basic'), typeName('StyleVariant')),
        value: option,
      });
    },
    serialize: (value: SerializeInput) => parseString(value) ?? defaultString,
    getVariants: (propId: string, prop: CodeComponentProp) => {
      const settings = parsePropSettings(prop.settings, prop.type);
      if (!settings || !('options' in settings)) return null;
      return {
        [propId]: settings.options.map((variant: string) => ({
          id: variant,
          displayName: variant,
        })),
      };
    },
  },
  [PropType.Boolean]: {
    getType: ({prop}) => {
      const settings = parsePropSettings(prop.settings, prop.type);
      if (!settings || !('trueLabel' in settings || 'falseLabel' in settings)) {
        return TBoolean;
      }
      return withMeta(
        {
          ...(settings.trueLabel !== undefined
            ? {trueLabel: settings.trueLabel}
            : {}),
          ...(settings.falseLabel !== undefined
            ? {falseLabel: settings.falseLabel}
            : {}),
        },
        TBoolean
      );
    },
    isBindable: true,
    isLocalizable: true,
    reifyDefaultValue: (prop: CodeComponentProp) => {
      const jsonValue = toJson(prop.defaultValue);
      const boolean =
        jsonValue !== undefined ? getBoolean(jsonValue) : undefined;
      return EUnion({
        tag: boolean === true ? 'True' : 'False',
        value: ERecord({}),
      });
    },
    serialize: (value: SerializeInput) => parseBoolean(value) ?? defaultBoolean,
  },
  [PropType.TextNode]: {
    getType: ({prop}) => {
      const settings = parsePropSettings(prop.settings, prop.type);
      if (!settings || !('multiLine' in settings)) {
        return TList(TElement);
      }
      const isMultiLine = settings.multiLine;
      const elementTypeWithMeta = withMeta(
        {
          multiLine: isMultiLine ?? false,
        },
        TElement
      );
      return TList(elementTypeWithMeta);
    },
    isBindable: true,
    isLocalizable: true,
    reifyDefaultValue: (prop: CodeComponentProp, {initialId}) => {
      const parsedString = parseString(prop.defaultValue);
      return parseTextNodeDefaultValue(
        parsedString ?? defaultString,
        initialId
      );
    },
    // We don't serialize slot-type props
    serialize: () => undefined,
    getSlotExpression: (propId: string, prop: CodeComponentProp) => {
      return EElement({
        id: propId,
        type: qualify(Plugins.name('Builtin'), Component.name('DOM')),
        data: ERecord({
          text: EBoolean(true),
          tag: EEnum('span'),
          slot: EText(prop.name), // 'slot' must use the prop name
          children: EData([propId]),
        }),
      });
    },
  },
  [PropType.Attributes]: {
    getType: () =>
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('Attributes'))),
    isBindable: true,
    isLocalizable: true,
    // Mirrors native Props Management — Attributes props always start with an
    // empty list; see packages/systems/user-components/properties/getNewFieldDefaultValue.ts.
    reifyDefaultValue: () => EList([]),
    // The evaluated value is `Basic::Attributes` — a list of `{name, value}`
    // string records. Fold into a Record<string, string> for the component.
    serialize: (value: SerializeInput) => {
      const list = parseArray(value);
      if (!list) return {};
      const result: Record<string, string> = {};
      for (const item of list) {
        const obj = getObject(item);
        if (!obj) continue;
        const name = parseString(obj.name);
        const val = parseString(obj.value);
        if (name === undefined || val === undefined) continue;
        result[name] = val;
      }
      return result;
    },
  },
};
